import { Action, createAsyncAction, createReducer } from 'typesafe-actions';
import { produce } from 'immer';
import type { ReduxError } from '@universal/errors';
import { InvoiceType } from '../../../../types/InvoiceType';

// Actions
export enum Actions {
  GET_MY_INVOICES = 'naf/myInvoices/GET_MY_INVOICES',
  GET_MY_INVOICES_SUCCESS = 'naf/myInvoices/GET_MY_INVOICES_SUCCESS',
  GET_MY_INVOICES_FAIL = 'naf/myInvoices/GET_MY_INVOICES_FAIL',
  GET_MY_INVOICES_CANCEL = 'naf/myInvoices/GET_MY_INVOICES_CANCEL',
}

export interface State {
  data: {
    invoices: Record<InvoiceType['invoiceNumber'], InvoiceType>;
    unpaid: Record<InvoiceType['invoiceNumber'], InvoiceType>;
    paid: Record<InvoiceType['invoiceNumber'], InvoiceType>;
    directDebetUrl: string | null;
  };
  isUpdating: boolean;
  fetchState: 'initial' | 'loading' | 'success' | 'error';
  errorState?: any;
}

export const initialState: State = {
  data: {
    invoices: {},
    unpaid: {},
    paid: {},
    directDebetUrl: null,
  },
  isUpdating: false,
  fetchState: 'initial',
};

export const invoiceActions = {
  getMyInvoices: createAsyncAction(
    Actions.GET_MY_INVOICES, // request payload creator
    Actions.GET_MY_INVOICES_SUCCESS, // success payload creator
    Actions.GET_MY_INVOICES_FAIL, // failure payload creator
    Actions.GET_MY_INVOICES_CANCEL, // optional cancel payload creator
  )<undefined, InvoiceType[], ReduxError, undefined>(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(invoiceActions.getMyInvoices.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdating = true;
      draftState.fetchState = 'loading';
    }),
  )
  .handleAction(invoiceActions.getMyInvoices.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
      draftState.fetchState = 'success';
      draftState.data.invoices = action.payload.reduce(
        (acc: Record<InvoiceType['invoiceNumber'], InvoiceType>, cur) => {
          acc[cur.invoiceNumber] = cur;
          return acc;
        },
        {},
      );
      draftState.data.directDebetUrl = action.payload.length > 0 ? action.payload[0].directDebetUrl : null;
      draftState.data.unpaid = action.payload.reduce((acc: Record<InvoiceType['invoiceNumber'], InvoiceType>, cur) => {
        if (cur.invoiceStatus === 'open') {
          acc[cur.invoiceNumber] = cur;
        }
        return acc;
      }, {});
      draftState.data.paid = action.payload.reduce((acc: Record<InvoiceType['invoiceNumber'], InvoiceType>, cur) => {
        if (cur.invoiceStatus === 'paid') {
          acc[cur.invoiceNumber] = cur;
        }
        return acc;
      }, {});
    }),
  )
  .handleAction(invoiceActions.getMyInvoices.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
      draftState.fetchState = 'error';
      draftState.errorState = action.payload;
    }),
  )
  .handleAction(invoiceActions.getMyInvoices.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
      draftState.fetchState = 'initial';
    }),
  );
